import {
  OBS_POSTER_SUFFIX,
  REGEXP_ORIGIN,
  REGEXP_ORIGIN_YISHOU,
  REGEXP_VIDEO_TYPE,
} from "@/constant";
import type { OBSUploadData } from "@/types";
import type { UploadFile } from "antd";
import Md5 from "crypto-js/md5";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { mapKeys, pickBy, snakeCase } from "lodash-es";
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = (): void => {};

/**
 * 将对象 key 从驼峰转为下划线
 * eg: pageSize => page_size
 */
export const underline = (data: Record<string, unknown>) => {
  return mapKeys(data, (value, key) => {
    return snakeCase(key);
  });
};

/**
 * 将对象的 key 从驼峰转为下划线，返回新对象
 * 因为 antd 的 pagination 默认使用 current 作为分页参数
 * eg: pageSize => page_size，current => page
 *
 * @param data 转换的对象
 * @param keyMap 转换的值，默认将 current => page
 */
export const underlineReqParams = (
  data: Record<string, unknown>,
  keyMap: Partial<Record<string, string>> = { current: "page" },
): Record<string, unknown> => {
  return mapKeys(data, (value, key) => {
    return keyMap[key] ?? snakeCase(key);
  });
};

/**
 * 元素滑动到可见区域，可设置头部高度
 * @param id
 * @param headerHeight
 * @param ele
 */
export const scrollIntoVisible = (
  id: string,
  headerHeight = 64,
  ele: Window | Element = window,
) => {
  const target = document.getElementById(id);
  if (target) {
    const targetPosition = target?.getBoundingClientRect().top ?? 0;
    ele.scrollBy({
      top: targetPosition - headerHeight,
      behavior: "smooth",
    });
  }
};

/**
 * 图片路径转UploadFile
 */
export const generateImageToUploadFile = (
  url: string[] | string | undefined,
  obsUploadData: OBSUploadData,
): UploadFile[] => {
  const domain = obsUploadData?.domain as string;
  const reg = REGEXP_ORIGIN;
  if (url) {
    if (Array.isArray(url)) {
      return url.map((path) => {
        const hasOrigin = reg.exec(path) !== null;
        return {
          name: hasOrigin ? path.replace(reg, "") : path,
          url: hasOrigin ? path.replace(reg, "") : path,
          status:
            hasOrigin && !REGEXP_ORIGIN_YISHOU.test(path)
              ? "uploading"
              : "done",
          thumbUrl: hasOrigin ? path : `${domain}${path}`,
        } as UploadFile;
      });
    } else {
      const hasOrigin = reg.exec(url) !== null;
      return [
        {
          name: hasOrigin ? url.replace(reg, "") : url,
          url: hasOrigin ? url.replace(reg, "") : url,
          status:
            hasOrigin && !REGEXP_ORIGIN_YISHOU.test(url) ? "uploading" : "done",
          thumbUrl: hasOrigin ? url : `${domain}${url}`,
        } as UploadFile,
      ];
    }
  } else {
    return [];
  }
};

/**
 * 图片路径转UploadFile
 */
export const generateVideoToUploadFile = (
  url: string[] | string | undefined,
  obsUploadData: OBSUploadData,
): UploadFile<{ name: string; url: string }>[] => {
  console.log(url, obsUploadData);
  if (url) {
    const domain = obsUploadData?.domain as string;
    const reg = REGEXP_ORIGIN;
    if (Array.isArray(url)) {
      return url.map((path) => {
        const hasOrigin = reg.exec(path) !== null;
        const suffix = path.slice(path.lastIndexOf("."));
        const wholeUrl = hasOrigin ? path : `${domain}${path}`;
        return {
          uid: Md5(path).toString(),
          name: hasOrigin ? path.replace(reg, "") : path,
          url: hasOrigin ? path.replace(reg, "") : path,
          thumbUrl: REGEXP_VIDEO_TYPE.test(suffix)
            ? `${wholeUrl}${OBS_POSTER_SUFFIX}`
            : wholeUrl,
        } as UploadFile;
      });
    } else {
      const hasOrigin = reg.exec(url) !== null;
      const suffix = url.slice(url.lastIndexOf("."));
      const wholeUrl = hasOrigin ? url : `${domain}${url}`;
      return [
        {
          uid: Md5(url).toString(),
          name: hasOrigin ? url.replace(reg, "") : url,
          url: hasOrigin ? url.replace(reg, "") : url,
          thumbUrl: REGEXP_VIDEO_TYPE.test(suffix)
            ? `${wholeUrl}${OBS_POSTER_SUFFIX}`
            : wholeUrl,
        } as UploadFile,
      ];
    }
  } else {
    return [];
  }
};

/**
 * 从 uploadFile 提取 url
 * 注意：这里的 url 是不带域名的
 */
export const getUrlFromUploadFile = <T = unknown>(
  files: UploadFile<T>[] | undefined,
): string[] => {
  return files?.map((item) => item.url as string) ?? [];
};

/**
 * 文件非一手链接，空链接也返回否
 * @param fileList
 */
export const isNonYiShouFile = (
  fileList: UploadFile[] | UploadFile | undefined,
): boolean => {
  if (!fileList) {
    return false;
  }
  if (Array.isArray(fileList)) {
    return !fileList.every(
      (file) => !file || REGEXP_ORIGIN_YISHOU.test(file?.thumbUrl ?? ""),
    );
  } else {
    return !REGEXP_ORIGIN_YISHOU.test(fileList?.thumbUrl ?? "");
  }
};

/**
 * 秒级时间戳转Dayjs
 */
export const transferTimestampToDayjs = (
  timestamp?: number | string,
): Dayjs | undefined => {
  if (!timestamp) {
    return undefined;
  }
  if (typeof timestamp === "number" || /^\d+$/.test(timestamp)) {
    return dayjs(Number(timestamp) * 1000);
  } else {
    return dayjs(new Date(timestamp).getTime());
  }
};

/**
 * 获取半链接
 */
export const getPathFromUrl = (url: string): string => {
  return url.replace(REGEXP_ORIGIN, "");
};

/**
 * 去除对象中的 falsy 值
 */
export const removeFalsy = (
  obj: Record<string, unknown>,
  ...falsyValue: unknown[]
): Partial<Record<string, unknown>> => {
  const falsyList = [undefined, null, ...falsyValue];
  return pickBy(obj, (value) => !falsyList.includes(value));
};

/**
 * 对象值按 ascii 码排序
 * @param obj
 */
export const objSort = (
  obj: Record<string, unknown>,
): Record<string, unknown> => {
  const newObj: Record<string, unknown> = {};
  const keys = Object.keys(obj).sort();
  keys.forEach((key) => {
    newObj[key] = obj[key];
  });
  return newObj;
};

/**
 * 字符串化对象值
 */
export const stringifyObjectValue = (
  data: Record<string, unknown>,
): Record<string, string> => {
  const strObj: Record<string, string> = {};
  const keys = Object.keys(data);
  keys.forEach((key) => {
    if (
      ["Array", "Object"].includes(
        Object.prototype.toString.call(data[key]).slice(8, -1),
      )
    ) {
      strObj[key] = JSON.stringify(data[key]);
    } else {
      strObj[key] = data[key] as string;
    }
  });
  return strObj;
};
